// frontend/src/pages/Dashboard/index.tsx

import React from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/store'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Card } from '@/components/common/UI/Card'
import { Button } from '@/components/common/UI/Button'
import { VacancyCard } from '@/components/vacancy/VacancyCard'
import InterviewSlotPanel from '@/components/interview/InterviewSlotPanel'
import { formatDate } from '@/utils/helpers'
import VideoInterviewPanel from '@/components/video/VideoInterviewPanel'

const Dashboard: React.FC = () => {
  const { user } = useAuthStore()
  const { data: access } = useQuery({
    queryKey: ['billing', 'me'],
    queryFn: () => apiClient.get<any>('/v1/billing/me'),
    enabled: user?.role === 'candidate' || user?.role === 'employer',
  })

  // Получение статистики
  const { data: stats } = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => {
      if (user?.role === 'employer') {
        return apiClient.get('/v1/companies/my/stats')
      }
      return apiClient.get('/v1/users/me/stats')
    },
    enabled: !!user
  })

  // Получение последних вакансий (для кандидата)
  const { data: recentVacancies } = useQuery({
    queryKey: ['dashboard', 'recent-vacancies'],
    queryFn: async () => {
      return apiClient.get('/v1/vacancies', {
        params: { limit: 5, sort: 'created_at', order: 'desc' }
      })
    },
    enabled: user?.role === 'candidate'
  })

  // Получение последних откликов
  const { data: recentApplications } = useQuery({
    queryKey: ['dashboard', 'recent-applications'],
    queryFn: async () => {
      if (user?.role === 'candidate') {
        return apiClient.get('/v1/applications/my', {
          params: { limit: 5 }
        })
      }
      return apiClient.get('/v1/applications/company', {
        params: { limit: 5 }
      })
    },
    enabled: !!user
  })

  if (user?.role === 'candidate') {
    return (
      <div className="container-custom py-8">
        <h1 className="text-2xl font-bold mb-6">Личный кабинет</h1>

        <Card className="mb-6 overflow-hidden border-primary-100 bg-gradient-to-r from-primary-50 via-white to-amber-50 p-5 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div><p className="text-sm font-semibold text-primary-700">Тариф соискателя</p><h2 className="mt-1 text-lg font-bold text-secondary-900">{access?.plan_name || 'Узнайте о возможностях LenJob+'}</h2><p className="mt-1 max-w-2xl text-sm text-secondary-600">С подпиской Plus резюме поднимается выше в поиске работодателей и получает заметную отметку. Также доступны ежедневное поднятие и инструменты для подготовки к поиску.</p></div>
            <Link to="/subscriptions" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700"><span aria-hidden="true">★</span> Купить подписку</Link>
          </div>
        </Card>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="p-4">
            <p className="text-sm text-secondary-500">Резюме</p>
            <p className="text-2xl font-bold">{stats?.resumes_count || 0}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-secondary-500">Отклики</p>
            <p className="text-2xl font-bold">{stats?.applications_count || 0}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-secondary-500">Просмотры резюме</p>
            <p className="text-2xl font-bold">{stats?.resume_views || 0}</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h2 className="text-lg font-semibold mb-4">Недавние вакансии</h2>
            <div className="space-y-3">
              {recentVacancies?.items?.map((vacancy: any) => (
                <VacancyCard key={vacancy.id} vacancy={vacancy} compact />
              ))}
            </div>
          </div>
          <div>
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">Мои отклики</h2>
              <Link to="/applications" className="text-sm text-primary-700 hover:underline">Все отклики</Link>
            </div>
            <div className="space-y-3">
              {recentApplications?.items?.map((app: any) => (
                <ApplicationCard key={app.id} application={app} />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Employer dashboard
  return (
    <div className="container-custom py-8">
      <h1 className="text-2xl font-bold mb-6">Работодатель</h1>

      <Card className="mb-6 overflow-hidden border-primary-100 bg-gradient-to-r from-primary-50 via-white to-sky-50 p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div><p className="text-sm font-semibold text-primary-700">Тариф работодателя</p><h2 className="mt-1 text-lg font-bold text-secondary-900">{access?.plan_name || 'Больше возможностей для найма'}</h2><p className="mt-1 max-w-2xl text-sm text-secondary-600">Сравните лимиты вакансий, доступ к контактам кандидатов, подписки на резюме, расширенную аналитику и AI-помощник.</p></div>
          <Link to="/subscriptions" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700"><span aria-hidden="true">↗</span> Купить подписку</Link>
        </div>
      </Card>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-4">
          <p className="text-sm text-secondary-500">Вакансии</p>
          <p className="text-2xl font-bold">{stats?.total_vacancies || 0}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-secondary-500">Активные</p>
          <p className="text-2xl font-bold">{stats?.active_vacancies || 0}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-secondary-500">Отклики</p>
          <p className="text-2xl font-bold">{stats?.total_applications || 0}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-secondary-500">Новые</p>
          <p className="text-2xl font-bold">{stats?.new_applications || 0}</p>
        </Card>
      </div>

      <div className="flex gap-4 mb-6">
        <Button onClick={() => window.location.href = '/vacancies/create'}>
          Создать вакансию
        </Button>
        <Button variant="outline" onClick={() => window.location.href = '/resumes'}>
          Поиск кандидатов
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-lg font-semibold mb-4">Мои вакансии</h2>
          <div className="space-y-3">
            {stats?.recent_vacancies?.map((vacancy: any) => (
              <VacancyCard key={vacancy.id} vacancy={vacancy} compact />
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold mb-4">Недавние отклики</h2>
          <div className="space-y-3">
            {recentApplications?.items?.map((app: any) => (
              <ApplicationCard key={app.id} application={app} employer />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// Компонент карточки отклика
const ApplicationCard: React.FC<{ application: any; employer?: boolean }> = ({
  application,
  employer
}) => {
  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    review: 'bg-blue-100 text-blue-800',
    invited: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    offer: 'bg-purple-100 text-purple-800',
    hired: 'bg-emerald-100 text-emerald-800'
  }

  const statusLabels: Record<string, string> = {
    pending: 'На рассмотрении',
    review: 'Просмотрено',
    invited: 'Приглашение',
    rejected: 'Отказ',
    offer: 'Оффер',
    hired: 'Принят'
  }

  return (
    <Card className="p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <Link to={`/vacancies/${application.vacancy_id}`}>
            <h4 className="font-medium text-secondary-900 hover:text-primary-600 transition-colors">
              {application.vacancy?.title}
            </h4>
          </Link>
          <p className="text-sm text-secondary-600">
            {employer ? application.candidate?.full_name : application.company?.name}
          </p>
          <p className="text-xs text-secondary-400 mt-1">
            {formatDate(application.created_at)}
          </p>
        </div>
        <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[application.status]}`}>
          {statusLabels[application.status] || application.status}
        </span>
      </div>
      {(application.current_stage === 'interview' || application.status === 'invited') && (
        <>
          <InterviewSlotPanel applicationId={application.id} employer={Boolean(employer)} />
          <VideoInterviewPanel applicationId={application.id} employer={Boolean(employer)} />
        </>
      )}
    </Card>
  )
}

export default Dashboard
